<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="routes"
      :headers="routesHeaders"
      :list="routes"
      :hide-grouping="true"
      @add-new="onAddNewRoute"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData"
      clickable
      @table-item-clicked="onRouteClicked"
      table-id="routes"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton :icon="IconPencilSquare" @action="onEditRoute(item as Route)" />
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as Route)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <RouteModalCreateEdit
      v-if="createEditModalActive"
      :route="route"
      :mode="createEditModalMode"
      :title="modalEditCreateTitle()"
      :active-users="activeUsers"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';

import { getRoutes, removeRoutesByIds } from '@/services/api/routes';
import { filterActive, filterData, sortData } from '@/utils/table';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import RouteModalCreateEdit from '@/components/routes/RouteModalCreateEdit.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import IconPencilSquare from '@/assets/icons/IconPencilSquare.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import type { TableFilterData } from '@/types/Base';
import type { Route } from '@/types/Routes';
import router from '@/router';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();

const routes = ref<Partial<Route>[]>([]);
const filterIsActive = ref<boolean | undefined>();

const activeUsers = computed<string[]>(() => {
  const ids = (routes.value || [])
    .map((r) => r?.userId)
    .filter((id): id is string => !!id && id !== '')
    .map((id) => String(id));
  return [...new Set(ids)];
});

const routesHeaders = [
  { value: 'name', text: $t('routes.route'), type: 'text', active: true },
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'user.name', text: $t('routes.user'), type: 'text', active: true },
  { value: 'companySite.name', text: $t('routes.companySite'), type: 'text', active: true },
  { value: 'routeDefinition.name', text: $t('routes.routeDefinition'), type: 'text', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

const route = ref<Route>(initializeRoute());
const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');

function initializeRoute(): Route {
  return {
    id: uuidv4(),
    name: '',
    code: '',
    companySiteId: '',
    routeDefinitionId: '',
    sequence: 0,
    isSystem: false,
    isFromManager: true,
    isActive: true,
    userId: '',
  };
}

function onAddNewRoute() {
  route.value = initializeRoute();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEditRoute(item: Route) {
  route.value = item;
  createEditModalActive.value = true;
  createEditModalMode.value = 'edit';
}

async function onSave(modifiedData: Route) {
  if (!route.value) return;

  const index = routes.value.findIndex((item) => item.id === modifiedData.id);
  if (index !== -1) routes.value[index] = modifiedData;
  else routes.value.push(modifiedData);
  createEditModalActive.value = false;
  await loadData();
}

async function loadData(data?: TableFilterData) {
  let response = await getRoutes();
  if (!response) return;

  if (data && data.query)
    response = filterData(response, data.query, [
      'name',
      'code',
      'user.name',
      'companSite.name',
      'routeDefinition.name',
    ]);

  if (data && data.column !== undefined && data.order)
    response = sortData(response, data.column, data.order, routesHeaders);

  if (data && data.isActive !== undefined) response = filterActive(response, data.isActive);

  routes.value = response;
}

async function onDeleteMultiselect(items: Route[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  const res = await removeRoutesByIds(idsToDelete);
  if (!res && routes.value)
    routes.value = routes.value.filter((route) => !route.id || !idsToDelete.includes(route.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeRoutesByIds([idToDelete]);
  if (!res && routes.value) routes.value = routes.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<Route>();
function onDeleteRow(item: Route) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function modalEditCreateTitle() {
  if (route.value && !route.value.isFromManager) return `${$t('common.actions.view')}  route `;
  return createEditModalMode.value === 'edit'
    ? `${$t('common.actions.edit')} route`
    : `${$t('common.actions.create')} route`;
}

function onRouteClicked(item: Route) {
  router.push({ name: 'route-details', params: { id: item.id } });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
